/**
 * Deterministic assembly for TransCelerate Patient Library Sections 5.4.1 and 5.4.2.
 */

import {
  PATIENT_LIBRARY_CBP_DEFINITION,
  PATIENT_LIBRARY_NCBP_DEFINITION,
} from '../data/transceleratePatientLibrarySection541.js';
import {
  PATIENT_LIBRARY_FEMALE_OPTIONS,
  PATIENT_LIBRARY_MALE_M1A,
  PATIENT_LIBRARY_MALE_M1B,
} from '../data/transceleratePatientLibrarySection542.js';

const SUPERSCRIPT_MAP = {
  a: 'ᵃ',
  b: 'ᵇ',
  c: 'ᶜ',
  d: 'ᵈ',
  e: 'ᵉ',
  f: 'ᶠ',
};

/** Convert Patient Library ^a^ markers to Unicode superscript for protocol output. */
export function patientLibraryFootnotesToSuperscript(text) {
  if (!text) return '';
  return String(text).replace(/\^([a-z])\^/gi, (_, ch) => {
    const lower = ch.toLowerCase();
    return SUPERSCRIPT_MAP[lower] || `^${ch}^`;
  });
}

export const DEFAULT_PATIENT_LIBRARY_DECISIONS = {
  childbearing_population_pathway: '',
  genotoxicity_risk: '',
  female_reproductive_risk: '',
  male_ejaculate_risk: '',
  has_study_specific_reproductive_requirements: false,
  study_specific_reproductive_requirements: '',
};

export const CHILD_BEARING_PATHWAY = {
  CBP: 'cbp',
  NCBP_ONLY: 'ncbp_only',
};

export const GENOTOXICITY_RISK = {
  YES: 'yes',
  NO: 'no',
};

export const FEMALE_REPRODUCTIVE_RISK = {
  DEMONSTRATED_SUSPECTED: 'demonstrated_suspected',
  POSSIBLE: 'possible',
  UNLIKELY: 'unlikely',
};

export const MALE_EJACULATE_RISK = {
  RISK_VIA_EJACULATE: 'risk_via_ejaculate',
  NO_RISK_VIA_EJACULATE: 'no_risk_via_ejaculate',
};

const FEMALE_RISK_TO_OPTION = {
  [FEMALE_REPRODUCTIVE_RISK.DEMONSTRATED_SUSPECTED]: 'F1B',
  [FEMALE_REPRODUCTIVE_RISK.POSSIBLE]: 'F2',
  [FEMALE_REPRODUCTIVE_RISK.UNLIKELY]: 'F3',
};

function normalizeDecisions(raw) {
  return {
    ...DEFAULT_PATIENT_LIBRARY_DECISIONS,
    ...(raw && typeof raw === 'object' ? raw : {}),
  };
}

/**
 * Build Section 5.4.1 content from pathway selection.
 * @param {'cbp' | 'ncbp_only' | ''} pathway
 */
export function buildSection541Content(pathway) {
  if (pathway === CHILD_BEARING_PATHWAY.CBP) {
    return patientLibraryFootnotesToSuperscript(PATIENT_LIBRARY_CBP_DEFINITION);
  }
  if (pathway === CHILD_BEARING_PATHWAY.NCBP_ONLY) {
    return patientLibraryFootnotesToSuperscript(PATIENT_LIBRARY_NCBP_DEFINITION);
  }
  return '';
}

/**
 * Documented reproductive requirements from intake (NCBP appendix / study-specific).
 */
export function extractDocumentedReproductiveRequirements(intake) {
  const sources = [
    intake?.target_population?.reproductive_status_contraception,
    intake?.template_language_fields?.patient_library_decisions?.study_specific_reproductive_requirements,
  ];
  return sources.map((s) => String(s || '').trim()).find(Boolean) || '';
}

function buildNcbpContraceptionStatement(intake) {
  const lines = [
    'Contraception requirements are not applicable because this study enrolls only Participants of Nonchildbearing Potential (NCBP), as defined in Section 5.4.1.',
  ];
  const documented = extractDocumentedReproductiveRequirements(intake);
  if (documented) {
    lines.push('');
    lines.push(documented);
  }
  return lines.join('\n');
}

function appendStudySpecificRequirements(blocks, decisions, intake) {
  if (!decisions.has_study_specific_reproductive_requirements) return;
  const appendix =
    String(decisions.study_specific_reproductive_requirements || '').trim() ||
    extractDocumentedReproductiveRequirements(intake);
  if (appendix) {
    blocks.push('');
    blocks.push(appendix);
  }
}

/**
 * Build Section 5.4.2 content per decision tree.
 * @returns {{ content: string, warnings: string[], complete: boolean }}
 */
export function buildSection542Content(decisionsInput, intake = {}) {
  const decisions = normalizeDecisions(decisionsInput);
  const warnings = [];
  const pathway = decisions.childbearing_population_pathway;

  if (pathway === CHILD_BEARING_PATHWAY.NCBP_ONLY) {
    return {
      content: buildNcbpContraceptionStatement(intake),
      warnings,
      complete: true,
    };
  }

  if (pathway !== CHILD_BEARING_PATHWAY.CBP) {
    return { content: '', warnings: ['Select participant population in Section 5.4.1.'], complete: false };
  }

  const blocks = [];

  if (decisions.genotoxicity_risk === GENOTOXICITY_RISK.YES) {
    blocks.push(PATIENT_LIBRARY_FEMALE_OPTIONS.F1A);
    if (PATIENT_LIBRARY_MALE_M1A) {
      blocks.push(PATIENT_LIBRARY_MALE_M1A);
    } else {
      warnings.push(
        'Male Option M1A Patient Library text is not configured. Add verbatim text to transceleratePatientLibrarySection542.js.'
      );
    }
  } else if (decisions.genotoxicity_risk === GENOTOXICITY_RISK.NO) {
    const femaleKey = FEMALE_RISK_TO_OPTION[decisions.female_reproductive_risk];
    if (!femaleKey) {
      warnings.push('Select female reproductive risk classification for Section 5.4.2.');
    } else {
      blocks.push(PATIENT_LIBRARY_FEMALE_OPTIONS[femaleKey]);
    }

    if (decisions.male_ejaculate_risk === MALE_EJACULATE_RISK.RISK_VIA_EJACULATE) {
      if (PATIENT_LIBRARY_MALE_M1B) {
        blocks.push(PATIENT_LIBRARY_MALE_M1B);
      } else {
        warnings.push(
          'Male Option M1B Patient Library text is not configured. Add verbatim text to transceleratePatientLibrarySection542.js.'
        );
      }
    }
  } else {
    warnings.push('Answer genotoxicity risk (Question 1) for Section 5.4.2.');
  }

  appendStudySpecificRequirements(blocks, decisions, intake);

  const content = blocks
    .filter(Boolean)
    .map((block) => patientLibraryFootnotesToSuperscript(block))
    .join('\n\n');

  let complete = warnings.length === 0 && blocks.length > 0;
  if (decisions.genotoxicity_risk === GENOTOXICITY_RISK.NO) {
    complete =
      complete &&
      Boolean(FEMALE_RISK_TO_OPTION[decisions.female_reproductive_risk]) &&
      Boolean(decisions.male_ejaculate_risk);
  } else if (decisions.genotoxicity_risk !== GENOTOXICITY_RISK.YES) {
    complete = false;
  }

  return { content, warnings, complete };
}

/**
 * Assemble both 5.4.1 and 5.4.2 from intake decisions.
 */
export function assemblePatientLibrarySection54(intake) {
  const decisions = normalizeDecisions(intake?.template_language_fields?.patient_library_decisions);
  const section541 = buildSection541Content(decisions.childbearing_population_pathway);
  const section542Result = buildSection542Content(decisions, intake);

  const warnings = [];
  if (!decisions.childbearing_population_pathway) {
    warnings.push('Select participant population for Section 5.4.1.');
  }
  warnings.push(...section542Result.warnings);

  return {
    definitions_childbearing_potential: section541,
    contraception_requirements: section542Result.content,
    warnings,
    complete:
      Boolean(decisions.childbearing_population_pathway) &&
      Boolean(section541) &&
      section542Result.complete,
  };
}

export function isPatientLibraryDeterministicField(fieldName) {
  return fieldName === 'definitions_childbearing_potential' || fieldName === 'contraception_requirements';
}
